import { memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CartDrawerCard from '../../Card/CartDrawerCard';

/**
 * Single cart item row
 * Memoized to prevent unnecessary re-renders
 */
const CartItemRow = memo(({ lineItem, index, orderType }) => {
  const cart = useSelector((state) => state.shopReducer.cart);
  const [errDelivery, setErrDelivery] = useState('');

  // Find matching cart item for additional details
  const matchingCartItem = useMemo(() => {
    if (!Array.isArray(cart) || cart.length === 0) return null;
    return cart.find((cartItem) => {
      return (
        cartItem.itemId === lineItem.variationId ||
        cartItem.title?.toLowerCase() === lineItem.name?.toLowerCase() ||
        cartItem.variationId === lineItem.variationId
      );
    });
  }, [cart, lineItem]);

  // Transform lineItem data
  const transformedItem = useMemo(() => {
    return {
      ...(matchingCartItem || {}),
      ...lineItem,
      itemId:
        lineItem.variationId ||
        matchingCartItem?.itemId ||
        `line-item-${index}`,
      title: lineItem.name || matchingCartItem?.title || 'Unknown Item',
      quantity: parseInt(lineItem.quantity) || 1,
      price: lineItem.grossSalesMoney
        ? (
            parseFloat(lineItem.grossSalesMoney) /
            100 /
            parseInt(lineItem.quantity || 1)
          ).toFixed(2)
        : matchingCartItem?.price || '0.00',
      calculatedPrice: lineItem.grossSalesMoney
        ? parseFloat(lineItem.grossSalesMoney) / 100
        : matchingCartItem?.calculatedPrice || 0,
      image: matchingCartItem?.image || '',
      modifiers: lineItem.modifiers || [],
      modifierDetails:
        lineItem.modifiers?.map((modifier) => ({
          optionTitle: modifier.name || 'Unknown Modifier',
          price: modifier.totalPriceMoney?.amount
            ? parseFloat(modifier.totalPriceMoney.amount) / 100
            : 0,
          quantity: parseInt(modifier.quantity) || 1,
          catalogObjectId: modifier.catalogObjectId,
          uid: modifier.uid,
          basePriceMoney: modifier.basePriceMoney,
        })) ||
        matchingCartItem?.modifierDetails ||
        [],
      variationId: lineItem.variationId || matchingCartItem?.variationId,
      variationName: lineItem.variationName || matchingCartItem?.variationName || '',
    };
  }, [lineItem, matchingCartItem, index]);

  return (
    <CartDrawerCard
      item={transformedItem}
      typeState={orderType}
      itemErr=""
      errDelivery={errDelivery}
      setErrDelivery={setErrDelivery}
      lineItemData={lineItem}
    />
  );
});

CartItemRow.displayName = 'CartItemRow';

export default CartItemRow;
